/**
 * 문제 데이터 쿼리 훅
 *
 * IndexedDB 캐시 → 서버 조회 → IndexedDB 저장 흐름 구현
 */

"use client";

import { useQuery, useQueries } from "@tanstack/react-query";
import { loadProblems } from "@/lib/load-problems";
import { useProblemCacheState } from "@/components/ProblemCacheInvalidator";
import type { ProblemData } from "@/components/ProblemDataContext";

/**
 * 다중 문제 데이터 조회 훅
 *
 * 1. IndexedDB에서 캐시 확인
 * 2. 캐시 미스만 서버에서 조회
 * 3. 새 데이터 IndexedDB에 저장
 * 4. 병합된 결과 반환
 */
export function useProblemsQuery(ids: string[]) {
  const cacheState = useProblemCacheState();

  return useQuery({
    // 정렬은 안정적인 쿼리 키를 만들기 위한 것. ids.sort() 는 in-place 라 호출부가 넘긴 배열
    // (MDXContentWrapper 의 memoized problemIds 등)을 알파벳순으로 변형시켜, 그 배열을 문서
    // 순서로 쓰는 곳(예: firstProblemId = problemIds[0] → "문제 풀이로 이동" 첫 문제 앵커)이
    // 깨진다. 복사본을 정렬해 입력 배열의 순서를 보존한다.
    queryKey: ["problems", [...ids].sort().join(",")],
    queryFn: () => loadProblems(ids, cacheState === "ready"),
    // 동기화 중인 서버 우회 query를 캐시 포맷 초기화가 취소하지 않도록 표시한다.
    meta: { problemCacheMode: cacheState },
    enabled: cacheState !== "checking" && ids.length > 0,
    retry: 1,
    staleTime: 24 * 60 * 60 * 1000, // 24시간
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7일
  });
}

/**
 * 단일 문제 데이터 조회 훅
 */
export function useProblemQuery(id: string, enabled = true) {
  const cacheState = useProblemCacheState();

  return useQuery({
    queryKey: ["problem", id],
    meta: { problemCacheMode: cacheState },
    queryFn: async () => {
      const problems = await loadProblems([id], cacheState === "ready");
      if (!problems[id]) {
        throw new Error(`문제를 찾을 수 없습니다: ${id}`);
      }
      return problems[id];
    },
    enabled: cacheState !== "checking" && !!id && enabled,
    retry: 1,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
  });
}

/**
 * 다중 문제를 개별 쿼리로 조회 (useQueries 사용)
 * - 각 문제가 독립적인 캐시를 가짐
 * - 일부 문제만 리패치 가능
 */
export function useProblemQueries(ids: string[]) {
  const cacheState = useProblemCacheState();

  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ["problem", id],
      meta: { problemCacheMode: cacheState },
      enabled: cacheState !== "checking" && !!id,
      queryFn: async () => {
        const problems = await loadProblems([id], cacheState === "ready");
        if (!problems[id]) {
          throw new Error(`문제를 찾을 수 없습니다: ${id}`);
        }
        return problems[id];
      },
      retry: 1,
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 7 * 24 * 60 * 60 * 1000,
    })),
    combine: (results) => {
      const problems: Record<string, ProblemData | null> = {};
      const isLoading = results.some((r) => r.isLoading);
      const isError = results.some((r) => r.isError);

      results.forEach((result, index) => {
        problems[ids[index]] = result.data ?? null;
      });

      return {
        problems,
        isLoading,
        isError,
      };
    },
  });
}
